import React, { useMemo, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { TaskTemplate, TaskTemplateData } from '@models/taskTemplate.model';
import { formatForDisplay } from '@utils/formatDate';
import { cn } from '@utils/cn';
import {
  Layers,
  Calendar,
  Clock,
  DollarSign,
  Package,
  Wrench,
  ChevronDown,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { htmlToPlainText } from '@utils/richText';

interface TemplateDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: TaskTemplate | null;
}

interface TaskTreeItemProps {
  task: TaskTemplateData;
  subtasks: TaskTemplateData[];
  isLast: boolean;
}

const TaskTreeItem: React.FC<TaskTreeItemProps> = ({
  task,
  subtasks,
  isLast,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasSubtasks = subtasks.length > 0;
  const hasMaterials = (task.materials?.length || 0) > 0;
  const hasEquipment = (task.equipments?.length || 0) > 0;

  return (
    <div className={cn(!isLast && 'mb-2')}>
      {/* Parent task */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        {/* Expand toggle */}
        <button type="button"
          onClick={() => hasSubtasks && setIsExpanded(!isExpanded)}
          className={cn(
            'mt-0.5 p-0.5 rounded transition-colors flex-shrink-0',
            hasSubtasks
              ? 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer'
              : 'text-transparent cursor-default'
          )}>
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>

        {/* Task info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {task.name}
            </span>
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 uppercase flex-shrink-0">
              {task.taskType}
            </span>
          </div>

          {task.description && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2 whitespace-pre-line">
              {htmlToPlainText(task.description)}
            </p>
          )}

          {/* Task metadata */}
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
            {task.duration != null && task.duration > 0 && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {task.duration} day{task.duration !== 1 ? 's' : ''}
              </span>
            )}
            {task.manHours != null && task.manHours > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {task.manHours}h
              </span>
            )}
            {task.budget != null && task.budget > 0 && (
              <span className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                ${task.budget.toLocaleString()}
              </span>
            )}
            {hasMaterials && (
              <span className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                {task.materials!.length} material
                {task.materials!.length !== 1 ? 's' : ''}
              </span>
            )}
            {hasEquipment && (
              <span className="flex items-center gap-1">
                <Wrench className="w-3 h-3" />
                {task.equipments!.length} equipment
              </span>
            )}
          </div>

          {/* Materials detail */}
          {hasMaterials && (
            <div className="mt-2 pl-2 border-l-2 border-orange-200 dark:border-orange-800">
              {task.materials!.map((mat, idx) => (
                <div
                  key={idx}
                  className="text-xs text-gray-500 dark:text-gray-400 py-0.5 flex items-center gap-2">
                  <Package className="w-2.5 h-2.5 text-orange-400" />
                  <span>{mat.name}</span>
                  {mat.amount != null && (
                    <span className="text-gray-400 dark:text-gray-500">
                      x{mat.amount} {mat.amountType || ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Equipment detail */}
          {hasEquipment && (
            <div className="mt-2 pl-2 border-l-2 border-purple-200 dark:border-purple-800">
              {task.equipments!.map((eq, idx) => (
                <div
                  key={idx}
                  className="text-xs text-gray-500 dark:text-gray-400 py-0.5 flex items-center gap-2">
                  <Wrench className="w-2.5 h-2.5 text-purple-400" />
                  <span>{eq.name}</span>
                  {eq.hoursUsed != null && (
                    <span className="text-gray-400 dark:text-gray-500">
                      {eq.hoursUsed}h
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Subtasks */}
      {hasSubtasks && isExpanded && (
        <div className="ml-6 mt-1 pl-3 border-l-2 border-gray-200 dark:border-gray-600 space-y-1">
          {subtasks.map((subtask) => (
            <div
              key={subtask.tempId}
              className="p-2.5 rounded-md bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                  {subtask.name}
                </span>
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 uppercase flex-shrink-0">
                  {subtask.taskType}
                </span>
              </div>

              {subtask.description && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 line-clamp-1 whitespace-pre-line">
                  {htmlToPlainText(subtask.description)}
                </p>
              )}

              <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-gray-400 dark:text-gray-500">
                {subtask.duration != null && subtask.duration > 0 && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-2.5 h-2.5" />
                    {subtask.duration}d
                  </span>
                )}
                {subtask.manHours != null && subtask.manHours > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />
                    {subtask.manHours}h
                  </span>
                )}
                {subtask.budget != null && subtask.budget > 0 && (
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-2.5 h-2.5" />
                    ${subtask.budget.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TemplateDetailModal: React.FC<TemplateDetailModalProps> = ({
  isOpen,
  onClose,
  template,
}) => {
  const { parentTasks, subtasksByParent, totals } = useMemo(() => {
    if (!template?.tasks)
      return {
        parentTasks: [],
        subtasksByParent: {} as Record<string, TaskTemplateData[]>,
        totals: {
          tasks: 0,
          subtasks: 0,
          manHours: 0,
          budget: 0,
          materials: 0,
          equipment: 0,
        },
      };

    const parents = template.tasks.filter((t) => !t.parentTempId);
    const grouped: Record<string, TaskTemplateData[]> = {};

    parents.forEach((parent) => {
      grouped[parent.tempId] = template.tasks.filter(
        (t) => t.parentTempId === parent.tempId
      );
    });

    let manHours = 0;
    let budget = 0;
    let materials = 0;
    let equipment = 0;

    template.tasks.forEach((task) => {
      manHours += task.manHours || 0;
      budget += task.budget || 0;
      materials += task.materials?.length || 0;
      equipment += task.equipments?.length || 0;
    });

    return {
      parentTasks: parents,
      subtasksByParent: grouped,
      totals: {
        tasks: parents.length,
        subtasks: template.tasks.length - parents.length,
        manHours,
        budget,
        materials,
        equipment,
      },
    };
  }, [template]);

  if (!template) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={template.name} size="large">
      {/* Template description & date */}
      <div className="mb-4">
        {template.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {template.description}
          </p>
        )}
        {template.createdAt && (
          <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Created {formatForDisplay(template.createdAt)}
          </p>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
          <div className="p-2 rounded-md bg-gray-200 dark:bg-gray-600">
            <Layers className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </div>
          <div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {totals.tasks}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Task{totals.tasks !== 1 ? 's' : ''}
              {totals.subtasks > 0 && (
                <span> (+{totals.subtasks} sub)</span>
              )}
            </p>
          </div>
        </div>

        {totals.manHours > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <div className="p-2 rounded-md bg-blue-200 dark:bg-blue-800">
              <Clock className="w-4 h-4 text-blue-600 dark:text-blue-300" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {totals.manHours}h
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Man Hours
              </p>
            </div>
          </div>
        )}

        {totals.budget > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
            <div className="p-2 rounded-md bg-green-200 dark:bg-green-800">
              <DollarSign className="w-4 h-4 text-green-600 dark:text-green-300" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                ${totals.budget.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Budget
              </p>
            </div>
          </div>
        )}

        {totals.materials > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20">
            <div className="p-2 rounded-md bg-orange-200 dark:bg-orange-800">
              <Package className="w-4 h-4 text-orange-600 dark:text-orange-300" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {totals.materials}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Material{totals.materials !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}

        {totals.equipment > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20">
            <div className="p-2 rounded-md bg-purple-200 dark:bg-purple-800">
              <Wrench className="w-4 h-4 text-purple-600 dark:text-purple-300" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {totals.equipment}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Equipment
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Task tree section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Tasks
        </h3>

        {parentTasks.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
            No tasks in this template
          </p>
        ) : (
          <div className="space-y-2">
            {parentTasks.map((task, index) => (
              <TaskTreeItem
                key={task.tempId}
                task={task}
                subtasks={subtasksByParent[task.tempId] || []}
                isLast={index === parentTasks.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TemplateDetailModal;
